import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { api } from "../../api/client";
import { Check, ArrowLeft, Flag } from "lucide-react";
import { MOBILE } from "../../constants/testIds";

export default function Stopped() {
    const { entryId } = useParams();
    const nav = useNavigate();
    const loc = useLocation();
    const isOffline = new URLSearchParams(loc.search).get("offline") === "1";
    const [entry, setEntry] = useState(null);
    const [job, setJob] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/v1/time-entries/${entryId}`);
                setEntry(data);
                if (data.job_id) {
                    const j = await api.get(`/jobs/${data.job_id}`).then((x) => x.data);
                    setJob(j);
                }
            } catch (_) {
                setEntry({ id: entryId, _offline: true });
            }
        })();
    }, [entryId]);

    return (
        <div data-testid={MOBILE.stopped} className="px-5 pt-6 pb-32 text-center">
            <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4"
                 style={{ background: "var(--km-green-l)", color: "var(--km-green-d)" }}>
                <Check size={30} strokeWidth={2.4} />
            </div>
            <div className="eyebrow">{isOffline ? "Queued" : "Saved"}</div>
            <h1 className="text-3xl font-extrabold tracking-[-0.03em] mt-1">
                {isOffline ? "Queued offline" : "Your hours are in"}
            </h1>
            <p className="text-sm text-muted mt-2 max-w-sm mx-auto">
                {isOffline
                    ? "We'll sync it when you're back in signal. Nothing gets lost."
                    : "Nice work. Office has it."}
            </p>

            {entry && !entry._offline && (
                <div className="km-card p-5 mt-6 text-left">
                    <Row label="Job" value={job ? `${job.job_number || "—"} · ${job.address}` : "Unallocated"} />
                    <Row label="Type" value={entry.work_type} />
                    <Row label="Duration" value={entry.duration_hrs != null ? `${entry.duration_hrs} hrs` : "—"} mono />
                    <Row label="Rate" value={entry.effective_rate ? `$${entry.effective_rate}/hr` : "—"} mono />
                    {entry.is_variation && (
                        <div className="mt-3 rounded-lg p-3 flex items-start gap-2"
                             style={{ background: "var(--km-red-l)", color: "var(--km-red-d)" }}>
                            <Flag size={14} className="mt-0.5" />
                            <div className="text-sm">
                                <b>Variation flagged</b>
                                {entry.variation_note && <div className="opacity-80 mt-0.5">{entry.variation_note}</div>}
                                <div className="text-xs opacity-70 mt-1">Shannon will call to find out.</div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <button
                data-testid={MOBILE.stoppedBack}
                onClick={() => nav("/m/today")}
                className="mt-8 w-full h-12 rounded-2xl bg-[color:var(--ink-1)] text-white font-medium flex items-center justify-center gap-2"
            >
                <ArrowLeft size={16} /> Back to today
            </button>
        </div>
    );
}

function Row({ label, value, mono }) {
    return (
        <div className="flex justify-between py-2 border-b border-km last:border-b-0">
            <span className="text-xs uppercase tracking-[0.12em] text-muted">{label}</span>
            <span className={mono ? "num text-sm text-ink" : "text-sm text-ink"}>{value}</span>
        </div>
    );
}
